import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { trackEvent } from '../lib/api';
import {
  Shield,
  ArrowRight,
  Clock,
  Lock,
  Mail,
  Menu,
  X,
  Award,
  BarChart3,
  Download,
  ClipboardCheck,
} from 'lucide-react';
import { Footer } from '../components/layout/Footer';
import { Container } from '../components/layout/Container';

export function Landing() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Count the visit — this is the top of the funnel.
  useEffect(() => {
    trackEvent({ event: 'landing_page_view' });
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-white">
      {/* Fixed Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2 focus:outline-none">
              <img
                src="/logo.png"
                alt="AsiliGuide"
                className="w-28 sm:w-36 h-auto"
              />
              <span className="text-sm sm:text-lg font-semibold text-gray-900">
                Compliance Snapshot
              </span>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-6">
              <a
                href="https://asiliguide.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-gray-600 hover:text-gray-900 transition"
              >
                Main Platform
              </a>
              <a
                href="mailto:support@getasili.com"
                className="text-sm text-gray-600 hover:text-gray-900 transition"
              >
                Support
              </a>
              <Link
                to="/preview"
                className="px-4 py-2 rounded-xl text-sm font-medium text-white hover:opacity-90 transition"
                style={{ backgroundColor: '#009688' }}
              >
                Preview Checklist
              </Link>
            </nav>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 rounded-lg hover:bg-gray-50 transition"
            >
              {mobileMenuOpen ? (
                <X className="w-6 h-6 text-gray-600" />
              ) : (
                <Menu className="w-6 h-6 text-gray-600" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-gray-200 bg-white">
            <div className="px-4 py-3 space-y-3">
              <a
                href="https://asiliguide.com"
                target="_blank"
                rel="noopener noreferrer"
                className="block text-sm text-gray-600 hover:text-gray-900 py-2"
              >
                Main Platform
              </a>
              <a
                href="mailto:support@getasili.com"
                className="block text-sm text-gray-600 hover:text-gray-900 py-2"
              >
                Support
              </a>
              <Link
                to="/preview"
                onClick={() => setMobileMenuOpen(false)}
                className="block text-center px-4 py-2 rounded-xl text-sm font-medium text-white hover:opacity-90 transition"
                style={{ backgroundColor: '#009688' }}
              >
                Preview Checklist
              </Link>
            </div>
          </div>
        )}
      </header>

      <main className="flex-1 pt-16">
        {/* Hero Section */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
          <div className="text-center max-w-4xl mx-auto">
            {/* Badge */}
            <div
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border mb-6"
              style={{ backgroundColor: 'rgba(0, 150, 136, 0.05)', borderColor: 'rgba(0, 150, 136, 0.2)' }}
            >
              <Shield className="w-4 h-4" style={{ color: '#009688' }} />
              <span className="text-xs sm:text-sm font-medium" style={{ color: '#009688' }}>
                Home Care Compliance Assessment
              </span>
            </div>

            {/* Headline */}
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 tracking-tight mb-6">
              Is Your Agency{' '}
              <span style={{ color: '#009688' }}>Audit-Ready?</span>
            </h1>

            {/* Subheadline */}
            <p className="text-lg sm:text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
              A free compliance assessment for existing home care agencies.
              State-specific requirements. All 50 states + DC. Know your gaps before a surveyor finds them.
            </p>

            {/* Single CTA */}
            <div className="mb-4">
              <Link
                to="/checkout"
                className="inline-flex items-center justify-center gap-2 px-6 sm:px-8 py-3 sm:py-4 rounded-xl text-base sm:text-lg font-medium text-white hover:opacity-90 transition shadow-lg w-full sm:w-auto"
                style={{ backgroundColor: '#009688' }}
              >
                <span className="hidden sm:inline">Get Your Free Snapshot</span>
                <span className="sm:hidden">Free Snapshot</span>
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>

            {/* Trust line */}
            <p className="text-sm text-gray-500 mb-3">
              Current state regulations • 100% free • Instant results
            </p>

            {/* Preview link */}
            <Link
              to="/preview"
              className="text-sm font-medium hover:underline"
              style={{ color: '#009688' }}
            >
              Preview what's covered first →
            </Link>

            {/* Stats */}
            <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-6 max-w-3xl mx-auto">
              <div className="text-center flex flex-col justify-start">
                <p className="text-2xl sm:text-3xl font-bold" style={{ color: '#009688' }}>50 + DC</p>
                <p className="text-xs sm:text-sm text-gray-600">Nationwide</p>
              </div>
              <div className="text-center flex flex-col justify-start">
                <p className="text-2xl sm:text-3xl font-bold" style={{ color: '#009688' }}>7</p>
                <p className="text-xs sm:text-sm text-gray-600">Risk categories</p>
              </div>
              <div className="text-center flex flex-col justify-start">
                <p className="text-2xl sm:text-3xl font-bold" style={{ color: '#009688' }}>15 min</p>
                <p className="text-xs sm:text-sm text-gray-600">To complete</p>
              </div>
              <div className="text-center flex flex-col justify-start">
                <p className="text-2xl sm:text-3xl font-bold" style={{ color: '#009688' }}>Instant</p>
                <p className="text-xs sm:text-sm text-gray-600">On-screen results</p>
              </div>
            </div>
          </div>
        </section>

        {/* Cost of Non-Compliance Section - Strategic placement after hero */}
        <section className="bg-gray-900 py-12 sm:py-16">
          <Container>
            <div className="text-center mb-8">
              <p className="text-red-400 text-sm font-medium uppercase tracking-wide mb-2">
                The risk you can't afford to ignore
              </p>
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">
                What a Failed Audit <span className="text-red-400">Actually Costs</span>
              </h2>
              <p className="text-xs text-gray-500 mt-2">
                Industry-average figures reported by home-care operators and regulatory consultants.
              </p>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 max-w-4xl mx-auto mb-8">
              {[
                { cost: '$5,000+', label: 'Re-survey fees' },
                { cost: '$300/hr', label: 'Emergency consultants' },
                { cost: '$10,000+', label: 'Lost reimbursements' },
                { cost: 'License', label: 'Revocation risk' },
              ].map((item, i) => (
                <div key={i} className="text-center p-4 rounded-xl bg-gray-800/50 border border-gray-700">
                  <p className="text-xl sm:text-2xl font-bold text-red-400">{item.cost}</p>
                  <p className="text-sm text-gray-400">{item.label}</p>
                </div>
              ))}
            </div>

            <div className="text-center">
              <p className="text-gray-400 mb-2">
                Or know where you stand for <span className="text-white font-bold text-2xl">free</span>
                <br />
                <span className="text-gray-500">No consultants. No invoices. No surprises.</span>
              </p>
              <Link
                to="/checkout"
                className="inline-flex items-center justify-center gap-2 px-5 sm:px-6 py-3 rounded-xl text-gray-900 font-medium hover:opacity-90 transition bg-white w-full sm:w-auto"
              >
                Get Your Snapshot
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          </Container>
        </section>

        {/* What's In Your Report Section */}
        <section className="bg-gray-50 py-16 sm:py-20">
          <Container>
            <div className="text-center mb-12">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
                What's In Your Report
              </h2>
              <p className="text-gray-600 max-w-xl mx-auto">
                A gaps analysis that would take a consultant hours. Free.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
              {[
                {
                  icon: BarChart3,
                  title: 'Risk Classification',
                  desc: 'Know if you\'re safe or exposed — Low, Medium, or High risk rating',
                },
                {
                  icon: ClipboardCheck,
                  title: 'File Readiness Check',
                  desc: 'See if your caregiver files would pass inspection today',
                },
                {
                  icon: Shield,
                  title: 'Audit Flag Preview',
                  desc: 'See exactly what a surveyor would flag during an audit',
                },
                {
                  icon: Award,
                  title: 'Priority Fix List',
                  desc: 'Know what to fix first — items ranked by risk severity',
                },
                {
                  icon: Clock,
                  title: 'Future Risk Alerts',
                  desc: 'Stop problems before they become deficiencies',
                },
                {
                  icon: Download,
                  title: 'Shareable PDF Report',
                  desc: 'Share with your team, your consultant, or keep for records',
                },
              ].map((item, i) => (
                <div
                  key={i}
                  className="bg-white p-6 rounded-2xl border border-gray-200 hover:border-gray-300 hover:shadow-md transition"
                >
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                    style={{ backgroundColor: 'rgba(0, 150, 136, 0.1)' }}
                  >
                    <item.icon className="w-6 h-6" style={{ color: '#009688' }} />
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2">{item.title}</h3>
                  <p className="text-sm text-gray-600">{item.desc}</p>
                </div>
              ))}
            </div>
          </Container>
        </section>

        {/* How It Works Section */}
        <section className="py-16 sm:py-20">
          <Container>
            <div className="text-center mb-12">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
                How It Works
              </h2>
              <p className="text-gray-600">
                Three simple steps to your compliance report.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
              {[
                {
                  step: '1',
                  title: 'Enter Your Email',
                  desc: 'Select your state and provide your email. No payment needed.',
                },
                {
                  step: '2',
                  title: 'Complete Assessment',
                  desc: '15 minutes, at your own pace. Save and resume anytime.',
                },
                {
                  step: '3',
                  title: 'Get Your Report',
                  desc: 'See your risk score instantly on-screen; the full PDF lands in your inbox.',
                },
              ].map((item, i) => (
                <div key={i} className="text-center">
                  <div
                    className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4"
                    style={{ backgroundColor: 'rgba(0, 150, 136, 0.1)' }}
                  >
                    <span className="text-xl font-bold" style={{ color: '#009688' }}>{item.step}</span>
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2">{item.title}</h3>
                  <p className="text-sm text-gray-600">{item.desc}</p>
                </div>
              ))}
            </div>
          </Container>
        </section>

        {/* Trust Strip */}
        <section className="bg-gray-900 py-6">
          <Container>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8 text-sm text-gray-400">
              <div className="flex items-center gap-2">
                <Lock className="w-4 h-4" />
                <span>Your data is secure</span>
              </div>
              <a
                href="mailto:support@getasili.com"
                className="flex items-center gap-2 hover:text-white transition"
              >
                <Mail className="w-4 h-4" />
                <span>support@getasili.com</span>
              </a>
            </div>
          </Container>
        </section>

        {/* Final CTA Section */}
        <section className="py-16 sm:py-20 bg-gradient-to-b from-white to-gray-50">
          <Container>
            <div className="max-w-2xl mx-auto text-center">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
                Know Where You Stand
              </h2>
              <p className="text-gray-600 mb-8">
                A gaps analysis that would take a consultant hours. Free.
              </p>
              <Link
                to="/checkout"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl text-lg font-medium text-white hover:opacity-90 transition shadow-lg"
                style={{ backgroundColor: '#009688' }}
              >
                Get Your Free Snapshot
                <ArrowRight className="w-5 h-5" />
              </Link>
              <p className="text-sm text-gray-500 mt-4">
                No payment required. Find your gaps before a surveyor does.
              </p>
            </div>
          </Container>
        </section>
      </main>

      <Footer />
    </div>
  );
}
